// pages/Settings.jsx
import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  Upload,
  Users,
  Zap,
  Briefcase,
  FileText,
  User,
  Settings as SettingsIcon,
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  AlertCircle,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Calendar,
  DollarSign,
  CreditCard,
  Download,
} from "lucide-react";
import { Listbox, Transition } from "@headlessui/react";
import HubSpotConfigModal from "../../components/HubSpotConfigModal";
import HubSpotSyncLogsModal from "../../components/HubSpotSyncLogsModal";
import ZohoConfigModal from "../../components/ZohoConfigModal";
import ZohoSyncLogs from "../../components/ZohoSyncLogs";
import apiService from "../../services/api";

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [toggleStates, setToggleStates] = useState({
    Salesforce: false,
    Slack: false,
    Stripe: false,
    HubSpot: false,
    Zoho: false,
  });

  // HubSpot-specific state
  const [hubspotStatus, setHubspotStatus] = useState({
    connected: false,
    integration: null,
  });
  const [isHubSpotConfigOpen, setIsHubSpotConfigOpen] = useState(false);
  const [isHubSpotLogsOpen, setIsHubSpotLogsOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Zoho-specific state
  const [zohoStatus, setZohoStatus] = useState({
    connected: false,
    integration: null,
  });
  const [isZohoConfigOpen, setIsZohoConfigOpen] = useState(false);
  const [isZohoLogsOpen, setIsZohoLogsOpen] = useState(false);
  const [isZohoSyncing, setIsZohoSyncing] = useState(false);

  // General settings state
  const [companyData, setCompanyData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    website: "",
    industry: "",
    size: "",
    logo: null,
  });

  // Personal details state
  const [personalData, setPersonalData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    position: "",
    department: "",
    profileImage: null,
  });

  // Team members state
  const [teamMembers, setTeamMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");

  // Billing state
  const [billingInfo, setBillingInfo] = useState({
    plan: "Pro",
    billingCycle: "monthly",
    nextBillingDate: "2024-12-15",
    amount: 99,
    paymentMethod: {
      type: "card",
      last4: "4242",
      expiry: "12/25",
    },
  });

  const tabs = [
    { id: "general", label: "General" },
    { id: "team", label: "Team" },
    { id: "integrations", label: "Integrations" },
    { id: "billing", label: "Billing" },
  ];

  const integrations = [
    { id: "hubspot", name: "HubSpot", logo: "/assets/hubspot.png" },
    {
      id: "zoho",
      name: "Zoho CRM",
      description: "Sync leads, accounts, contacts, deals, cases, and tasks",
      logo: "/assets/zoho.png",
      color: "orange",
      category: "crm",
    },
    {
      id: "salesforce",
      name: "Salesforce",
      description: "Sync leads, accounts, opportunities, cases, and tasks",
      logo: "/assets/salesforce.png",
      color: "blue",
      category: "crm",
      available: true,
      hasDetailPage: true, // Add this
    },
    { id: "slack", name: "Slack", logo: "/assets/slack.png" },
    { id: "stripe", name: "Stripe", logo: "/assets/stripe.png" },
  ];

  const industries = [
    "Technology",
    "Finance",
    "Healthcare",
    "Retail",
    "Manufacturing",
    "Education",
    "Real Estate",
    "Other",
  ];

  const companySizes = [
    "1-10",
    "11-50",
    "51-200",
    "201-500",
    "501-1000",
    "1000+",
  ];
  const [salesforceStatus, setSalesforceStatus] = useState({
    connected: false,
    integration: null,
  });

  useEffect(() => {
    if (activeTab === "general") {
      fetchCompanyData();
    } else if (activeTab === "team") {
      fetchTeamMembers();
    } else if (activeTab === "integrations") {
      fetchHubSpotStatus();
      fetchZohoStatus();
      fetchSalesforceStatus(); // Add this
    }
  }, [activeTab]);

  // Add this function with other fetch functions (around line 230)
  const fetchSalesforceStatus = async () => {
    try {
      const response = await apiService.getSalesforceStatus();
      setSalesforceStatus(response);

      if (response.connected) {
        setToggleStates((prev) => ({
          ...prev,
          Salesforce: true,
        }));
      }
    } catch (error) {
      console.error("Error fetching Salesforce status:", error);
    }
  };
  const handleSalesforceConnect = async () => {
    try {
      const response = await apiService.getSalesforceAuthUrl();

      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const authWindow = window.open(
        response.authUrl,
        "Salesforce Authorization",
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      const checkWindow = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkWindow);
          fetchSalesforceStatus();
        }
      }, 500);

      const handleMessage = (event) => {
        if (event.data.type === "salesforce-connected") {
          fetchSalesforceStatus();
          window.removeEventListener("message", handleMessage);
        }
      };
      window.addEventListener("message", handleMessage);
    } catch (error) {
      console.error("Error connecting Salesforce:", error);
      alert("Failed to connect Salesforce. Please try again.");
    }
  };
  const fetchCompanyData = async () => {
    try {
      const response = await apiService.getMe();
      if (response.company) {
        setCompanyData({
          name: response.company.name || "",
          email: response.company.email || "",
          phone: response.company.phone || "",
          address: response.company.address || "",
          city: response.company.city || "",
          state: response.company.state || "",
          zipCode: response.company.zipCode || "",
          country: response.company.country || "",
          website: response.company.website || "",
          industry: response.company.industry || "",
          size: response.company.size || "",
          logo: response.company.logo || null,
        });
      }
      if (response.user) {
        setPersonalData({
          firstName: response.user.firstName || "",
          lastName: response.user.lastName || "",
          email: response.user.email || "",
          phone: response.user.phone || "",
          position: response.user.position || "",
          department: response.user.department || "",
          profileImage: response.user.profileImage || null,
        });
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setTeamMembers(response.users || []);
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };

  const fetchHubSpotStatus = async () => {
    try {
      const response = await apiService.getHubSpotStatus();
      setHubspotStatus(response);

      if (response.connected) {
        setToggleStates((prev) => ({
          ...prev,
          HubSpot: true,
        }));
      }
    } catch (error) {
      console.error("Error fetching HubSpot status:", error);
    }
  };

  const fetchZohoStatus = async () => {
    try {
      const response = await apiService.getZohoStatus();
      setZohoStatus(response);

      if (response.connected) {
        setToggleStates((prev) => ({
          ...prev,
          Zoho: true,
        }));
      }
    } catch (error) {
      console.error("Error fetching Zoho status:", error);
    }
  };

  const handleCompanyUpdate = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateCompanyData(companyData);
      alert("Company details updated successfully!");
    } catch (error) {
      console.error("Error updating company:", error);
      alert("Failed to update company details. Please try again.");
    }
  };

  const handlePersonalUpdate = async (e) => {
    e.preventDefault();
    try {
      await apiService.updatePersonalDetails(personalData);
      alert("Personal details updated successfully!");
    } catch (error) {
      console.error("Error updating personal details:", error);
      alert("Failed to update personal details. Please try again.");
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("logo", file);
      try {
        const response = await apiService.uploadCompanyLogo(formData);
        setCompanyData((prev) => ({ ...prev, logo: response.logoUrl }));
        alert("Logo uploaded successfully!");
      } catch (error) {
        console.error("Error uploading logo:", error);
        alert("Failed to upload logo. Please try again.");
      }
    }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("profileImage", file);
      try {
        const response = await apiService.uploadProfileImage(formData);
        setPersonalData((prev) => ({
          ...prev,
          profileImage: response.imageUrl,
        }));
        alert("Profile image uploaded successfully!");
      } catch (error) {
        console.error("Error uploading profile image:", error);
        alert("Failed to upload profile image. Please try again.");
      }
    }
  };

  const handleInviteTeamMember = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;

    try {
      await apiService.inviteTeamMembers([inviteEmail]);
      setInviteEmail("");
      alert("Invitation sent successfully!");
      fetchTeamMembers();
    } catch (error) {
      console.error("Error inviting team member:", error);
      alert("Failed to send invitation. Please try again.");
    }
  };

  const handleRemoveTeamMember = async (userId) => {
    try {
      alert("Team member removed successfully!");
      fetchTeamMembers();
    } catch (error) {
      console.error("Error removing team member:", error);
      alert("Failed to remove team member. Please try again.");
    }
  };

  const handleHubSpotConnect = async () => {
    try {
      const response = await apiService.getHubSpotAuthUrl();

      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const authWindow = window.open(
        response.authUrl,
        "HubSpot Authorization",
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      const checkWindow = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkWindow);
          fetchHubSpotStatus();
        }
      }, 500);

      const handleMessage = (event) => {
        if (event.data.type === "hubspot-connected") {
          fetchHubSpotStatus();
          window.removeEventListener("message", handleMessage);
        }
      };
      window.addEventListener("message", handleMessage);
    } catch (error) {
      console.error("Error connecting HubSpot:", error);
      alert("Failed to connect HubSpot. Please try again.");
    }
  };

  const handleHubSpotDisconnect = async () => {
    try {
      await apiService.disconnectHubSpot();
      setHubspotStatus({ connected: false, integration: null });
      setToggleStates((prev) => ({
        ...prev,
        HubSpot: false,
      }));
      alert("HubSpot disconnected successfully");
    } catch (error) {
      console.error("Error disconnecting HubSpot:", error);
      alert("Failed to disconnect HubSpot. Please try again.");
    }
  };

  const handleHubSpotSync = async () => {
    setIsSyncing(true);
    try {
      await apiService.syncHubSpot();
      alert("Sync started! Check sync logs for progress.");

      const pollInterval = setInterval(async () => {
        try {
          const response = await apiService.getHubSpotStatus();
          setHubspotStatus(response);

          if (response.integration?.lastSyncStatus !== "in_progress") {
            clearInterval(pollInterval);
            setIsSyncing(false);
          }
        } catch (error) {
          console.error("Error polling sync status:", error);
        }
      }, 3000);

      setTimeout(() => {
        clearInterval(pollInterval);
        setIsSyncing(false);
      }, 300000);
    } catch (error) {
      console.error("Error syncing HubSpot:", error);
      alert("Failed to start sync. Please try again.");
      setIsSyncing(false);
    }
  };

  // Zoho handlers
  const handleZohoConnect = async () => {
    try {
      const response = await apiService.getZohoAuthUrl();

      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const authWindow = window.open(
        response.authUrl,
        "Zoho CRM Authorization",
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      const checkWindow = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkWindow);
          fetchZohoStatus();
        }
      }, 500);

      const handleMessage = (event) => {
        if (event.data.type === "zoho-connected") {
          fetchZohoStatus();
          window.removeEventListener("message", handleMessage);
        }
      };
      window.addEventListener("message", handleMessage);
    } catch (error) {
      console.error("Error connecting Zoho CRM:", error);
      alert("Failed to connect Zoho CRM. Please try again.");
    }
  };

  const handleZohoDisconnect = async () => {
    try {
      await apiService.disconnectZoho();
      setZohoStatus({ connected: false, integration: null });
      setToggleStates((prev) => ({
        ...prev,
        Zoho: false,
      }));
      alert("Zoho CRM disconnected successfully");
    } catch (error) {
      console.error("Error disconnecting Zoho CRM:", error);
      alert("Failed to disconnect Zoho CRM. Please try again.");
    }
  };

  const handleZohoSync = async () => {
    setIsZohoSyncing(true);
    try {
      await apiService.syncZoho();
      alert("Sync started! Check sync logs for progress.");

      const pollInterval = setInterval(async () => {
        try {
          const response = await apiService.getZohoStatus();
          setZohoStatus(response);

          if (response.integration?.lastSyncStatus !== "in_progress") {
            clearInterval(pollInterval);
            setIsZohoSyncing(false);
          }
        } catch (error) {
          console.error("Error polling sync status:", error);
        }
      }, 3000);

      setTimeout(() => {
        clearInterval(pollInterval);
        setIsZohoSyncing(false);
      }, 300000);
    } catch (error) {
      console.error("Error syncing Zoho CRM:", error);
      alert("Failed to start sync. Please try again.");
      setIsZohoSyncing(false);
    }
  };

  const handleToggle = (integrationName) => {
    if (integrationName === "HubSpot") {
      if (hubspotStatus.connected) {
        handleHubSpotDisconnect();
      } else {
        handleHubSpotConnect();
      }
    } else if (integrationName === "Zoho CRM") {
      if (zohoStatus.connected) {
        handleZohoDisconnect();
      } else {
        handleZohoConnect();
      }
    } else {
      setToggleStates((prev) => ({
        ...prev,
        [integrationName]: !prev[integrationName],
      }));
    }
  };

  const getSyncStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "failed":
      case "error":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "in_progress":
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case "never":
        return <Clock className="w-4 h-4 text-gray-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
  };

  const formatLastSync = (date) => {
    if (!date) return "Never";
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const getLogoForIntegration = (integrationId) => {
    const integration = integrations.find((int) => int.id === integrationId);
    return integration?.logo || "/assets/default-integration.png";
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "general":
        return (
          <div className="space-y-8">
            {/* Company Information */}
            <div className="bg-[#0C0E12] border border-[#373A41] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <Building2 className="w-6 h-6 text-[#7F56D9]" />
                <h3 className="text-white text-lg font-medium">
                  Company Information
                </h3>
              </div>

              <form onSubmit={handleCompanyUpdate} className="space-y-6">
                {/* Company Logo */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Company Logo
                  </label>
                  <div className="flex items-center gap-4">
                    {companyData.logo ? (
                      <img
                        src={companyData.logo}
                        alt="Company logo"
                        className="w-20 h-20 rounded-lg object-cover bg-white"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-[#1a1d23] flex items-center justify-center">
                        <Building2 className="w-8 h-8 text-gray-500" />
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <div className="px-4 py-2 bg-[#7F56D9] hover:bg-[#6B46C1] text-white rounded-lg transition-colors flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Upload Logo
                      </div>
                    </label>
                  </div>
                </div>

                {/* Company Name & Industry */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={companyData.name}
                      onChange={(e) =>
                        setCompanyData((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="Acme Corporation"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Industry
                    </label>
                    <select
                      value={companyData.industry}
                      onChange={(e) =>
                        setCompanyData((prev) => ({
                          ...prev,
                          industry: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                    >
                      <option value="">Select Industry</option>
                      {industries.map((industry) => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={companyData.email}
                      onChange={(e) =>
                        setCompanyData((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="contact@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={companyData.phone}
                      onChange={(e) =>
                        setCompanyData((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>

                {/* Website & Company Size */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Website
                    </label>
                    <input
                      type="url"
                      value={companyData.website}
                      onChange={(e) =>
                        setCompanyData((prev) => ({
                          ...prev,
                          website: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="https://company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Company Size
                    </label>
                    <select
                      value={companyData.size}
                      onChange={(e) =>
                        setCompanyData((prev) => ({
                          ...prev,
                          size: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                    >
                      <option value="">Select Size</option>
                      {companySizes.map((size) => (
                        <option key={size} value={size}>
                          {size} employees
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={companyData.address}
                    onChange={(e) =>
                      setCompanyData((prev) => ({
                        ...prev,
                        address: e.target.value,
                      }))
                    }
                    className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                    placeholder="123 Main Street"
                  />
                </div>

                {/* City, State, Zip */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={companyData.city}
                      onChange={(e) =>
                        setCompanyData((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="San Francisco"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={companyData.state}
                      onChange={(e) =>
                        setCompanyData((prev) => ({
                          ...prev,
                          state: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="CA"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Zip Code
                    </label>
                    <input
                      type="text"
                      value={companyData.zipCode}
                      onChange={(e) =>
                        setCompanyData((prev) => ({
                          ...prev,
                          zipCode: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="94102"
                    />
                  </div>
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={companyData.country}
                    onChange={(e) =>
                      setCompanyData((prev) => ({
                        ...prev,
                        country: e.target.value,
                      }))
                    }
                    className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                    placeholder="United States"
                  />
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-700">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-[#7F56D9] hover:bg-[#6B46C1] text-white rounded-lg transition-colors"
                  >
                    Save Company Details
                  </button>
                </div>
              </form>
            </div>

            {/* Personal Details */}
            <div className="bg-[#0C0E12] border border-[#373A41] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <User className="w-6 h-6 text-[#7F56D9]" />
                <h3 className="text-white text-lg font-medium">
                  Personal Details
                </h3>
              </div>

              <form onSubmit={handlePersonalUpdate} className="space-y-6">
                {/* Profile Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Profile Image
                  </label>
                  <div className="flex items-center gap-4">
                    {personalData.profileImage ? (
                      <img
                        src={personalData.profileImage}
                        alt="Profile"
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-[#7F56D9] flex items-center justify-center text-white text-2xl font-semibold">
                        {personalData.firstName?.[0] || "U"}
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProfileImageUpload}
                        className="hidden"
                      />
                      <div className="px-4 py-2 bg-[#7F56D9] hover:bg-[#6B46C1] text-white rounded-lg transition-colors flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Upload Photo
                      </div>
                    </label>
                  </div>
                </div>

                {/* First Name & Last Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={personalData.firstName}
                      onChange={(e) =>
                        setPersonalData((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="John"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={personalData.lastName}
                      onChange={(e) =>
                        setPersonalData((prev) => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={personalData.email}
                      onChange={(e) =>
                        setPersonalData((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={personalData.phone}
                      onChange={(e) =>
                        setPersonalData((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                </div>

                {/* Position & Department */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Position
                    </label>
                    <input
                      type="text"
                      value={personalData.position}
                      onChange={(e) =>
                        setPersonalData((prev) => ({
                          ...prev,
                          position: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="Sales Manager"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Department
                    </label>
                    <input
                      type="text"
                      value={personalData.department}
                      onChange={(e) =>
                        setPersonalData((prev) => ({
                          ...prev,
                          department: e.target.value,
                        }))
                      }
                      className="w-full bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                      placeholder="Sales"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-700">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-[#7F56D9] hover:bg-[#6B46C1] text-white rounded-lg transition-colors"
                  >
                    Save Personal Details
                  </button>
                </div>
              </form>
            </div>
          </div>
        );

      case "team":
        return (
          <div className="space-y-6">
            {/* Invite Team Member */}
            <div className="bg-[#0C0E12] border border-[#373A41] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-[#7F56D9]" />
                <h3 className="text-white text-lg font-medium">
                  Invite Team Member
                </h3>
              </div>

              <form onSubmit={handleInviteTeamMember} className="flex gap-3">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="flex-1 bg-[#1a1d23] border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#7F56D9]"
                  placeholder="colleague@company.com"
                  required
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-[#7F56D9] hover:bg-[#6B46C1] text-white rounded-lg transition-colors"
                >
                  Send Invite
                </button>
              </form>
            </div>

            {/* Team Members List */}
            <div className="bg-[#0C0E12] border border-[#373A41] rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6 text-[#7F56D9]" />
                  <h3 className="text-white text-lg font-medium">
                    Team Members
                  </h3>
                </div>
                <span className="text-sm text-gray-400">
                  {teamMembers.length} members
                </span>
              </div>

              <div className="space-y-3">
                {teamMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-4 bg-[#1a1d23] rounded-lg hover:bg-[#22262F] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {member.profileImage ? (
                        <img
                          src={member.profileImage}
                          alt={member.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#7F56D9] flex items-center justify-center text-white font-semibold">
                          {member.name?.[0] || "U"}
                        </div>
                      )}
                      <div>
                        <div className="text-white font-medium">
                          {member.name}
                        </div>
                        <div className="text-sm text-gray-400">
                          {member.email}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-[#7F56D9]/20 text-[#7F56D9] rounded-full text-xs font-medium">
                        {member.role || "Member"}
                      </span>
                      <button
                        onClick={() => handleRemoveTeamMember(member.id)}
                        className="text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {teamMembers.length === 0 && (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                    <p className="text-gray-400">No team members yet</p>
                    <p className="text-sm text-gray-500">
                      Invite your first team member above
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case "integrations":
        return (
          <div className="min-h-screen">
            <div className="max-w-full mx-auto">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div>
                  <h2 className="text-white text-lg font-medium mb-1">
                    Integrations and connected apps
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Connect the tools you use everyday.
                  </p>
                </div>
                <div className="flex gap-3 w-full sm:w-auto">
                  <button className="w-full sm:w-auto px-8 py-2 bg-[#0C0E12] hover:bg-gray-700 text-white rounded-lg border border-gray-700 transition-colors">
                    Request Integration
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* HubSpot Integration Card */}
                <div className="bg-[#0C0E12] border-[#373A41] border rounded-lg p-5 flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                        <img
                          src="/assets/hubspot.png"
                          alt="HubSpot logo"
                          className="w-8 h-8 object-contain"
                        />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">HubSpot</h3>
                        {hubspotStatus.connected && (
                          <div className="flex items-center gap-1 mt-1">
                            {getSyncStatusIcon(
                              hubspotStatus.integration?.lastSyncStatus,
                            )}
                            <span className="text-xs text-gray-400">
                              {formatLastSync(
                                hubspotStatus.integration?.lastSyncAt,
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => handleToggle("HubSpot")}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        hubspotStatus.connected ? "bg-[#7F56D9]" : "bg-gray-600"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          hubspotStatus.connected
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  <p className="text-gray-400 text-sm flex-grow mb-4">
                    Sync contacts, deals, tickets, and tasks from HubSpot CRM
                  </p>

                  {hubspotStatus.connected && (
                    <div className="mb-4 p-3 bg-[#1a1d23] rounded-lg">
                      <div className="text-xs text-gray-400 mb-2">
                        Sync Configuration:
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {hubspotStatus.integration?.syncSettings
                          ?.syncContacts && (
                          <span className="px-2 py-1 bg-purple-900/30 text-purple-300 text-xs rounded">
                            Contacts
                          </span>
                        )}
                        {hubspotStatus.integration?.syncSettings?.syncDeals && (
                          <span className="px-2 py-1 bg-purple-900/30 text-purple-300 text-xs rounded">
                            Deals
                          </span>
                        )}
                        {hubspotStatus.integration?.syncSettings
                          ?.syncTickets && (
                          <span className="px-2 py-1 bg-purple-900/30 text-purple-300 text-xs rounded">
                            Tickets
                          </span>
                        )}
                        {hubspotStatus.integration?.syncSettings?.syncTasks && (
                          <span className="px-2 py-1 bg-purple-900/30 text-purple-300 text-xs rounded">
                            Tasks
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {hubspotStatus.connected && (
                    <div className="flex gap-2 border-t border-[#22262F] pt-4">
                      <button
                        onClick={() => setIsHubSpotConfigOpen(true)}
                        className="flex-1 text-sm text-gray-400 hover:text-white transition-colors py-2 px-3 bg-[#1a1d23] rounded-lg"
                      >
                        Configure
                      </button>
                      <button
                        onClick={handleHubSpotSync}
                        disabled={isSyncing}
                        className="flex-1 text-sm text-white transition-colors py-2 px-3 bg-[#7F56D9] hover:bg-[#6B46C1] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSyncing ? (
                          <>
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Syncing...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-3 h-3" />
                            Sync Now
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setIsHubSpotLogsOpen(true)}
                        className="flex-1 text-sm text-gray-400 hover:text-white transition-colors py-2 px-3 bg-[#1a1d23] rounded-lg"
                      >
                        View Logs
                      </button>
                    </div>
                  )}

                  {!hubspotStatus.connected && (
                    <button
                      onClick={handleHubSpotConnect}
                      className="text-gray-400 hover:text-white text-sm transition-colors text-end mt-4 border-t border-[#22262F] pt-5"
                    >
                      Connect HubSpot →
                    </button>
                  )}
                </div>

                {/* Zoho CRM Integration Card */}
                <div className="bg-[#0C0E12] border-[#373A41] border rounded-lg p-5 flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                        <img
                          src="/assets/zoho.png"
                          alt="Zoho CRM logo"
                          className="w-8 h-8 object-contain"
                        />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">Zoho CRM</h3>
                        {zohoStatus.connected && (
                          <div className="flex items-center gap-1 mt-1">
                            {getSyncStatusIcon(
                              zohoStatus.integration?.lastSyncStatus,
                            )}
                            <span className="text-xs text-gray-400">
                              {formatLastSync(
                                zohoStatus.integration?.lastSyncAt,
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => handleToggle("Zoho CRM")}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        zohoStatus.connected ? "bg-[#7F56D9]" : "bg-gray-600"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          zohoStatus.connected
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  <p className="text-gray-400 text-sm flex-grow mb-4">
                    Sync leads, accounts, contacts, deals, cases, and tasks from
                    Zoho CRM
                  </p>

                  {zohoStatus.connected && (
                    <div className="mb-4 p-3 bg-[#1a1d23] rounded-lg">
                      <div className="text-xs text-gray-400 mb-2">
                        Sync Configuration:
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {zohoStatus.integration?.syncSettings?.syncLeads && (
                          <span className="px-2 py-1 bg-orange-900/30 text-orange-300 text-xs rounded">
                            Leads
                          </span>
                        )}
                        {zohoStatus.integration?.syncSettings?.syncAccounts && (
                          <span className="px-2 py-1 bg-orange-900/30 text-orange-300 text-xs rounded">
                            Accounts
                          </span>
                        )}
                        {zohoStatus.integration?.syncSettings?.syncContacts && (
                          <span className="px-2 py-1 bg-orange-900/30 text-orange-300 text-xs rounded">
                            Contacts
                          </span>
                        )}
                        {zohoStatus.integration?.syncSettings?.syncDeals && (
                          <span className="px-2 py-1 bg-orange-900/30 text-orange-300 text-xs rounded">
                            Deals
                          </span>
                        )}
                        {zohoStatus.integration?.syncSettings?.syncCases && (
                          <span className="px-2 py-1 bg-orange-900/30 text-orange-300 text-xs rounded">
                            Cases
                          </span>
                        )}
                        {zohoStatus.integration?.syncSettings?.syncTasks && (
                          <span className="px-2 py-1 bg-orange-900/30 text-orange-300 text-xs rounded">
                            Tasks
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {zohoStatus.connected && (
                    <div className="flex gap-2 border-t border-[#22262F] pt-4">
                      <button
                        onClick={() => setIsZohoConfigOpen(true)}
                        className="flex-1 text-sm text-gray-400 hover:text-white transition-colors py-2 px-3 bg-[#1a1d23] rounded-lg"
                      >
                        Configure
                      </button>
                      <button
                        onClick={handleZohoSync}
                        disabled={isZohoSyncing}
                        className="flex-1 text-sm text-white transition-colors py-2 px-3 bg-[#7F56D9] hover:bg-[#6B46C1] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isZohoSyncing ? (
                          <>
                            <RefreshCw className="w-3 h-3 animate-spin" />
                            Syncing...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-3 h-3" />
                            Sync Now
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setIsZohoLogsOpen(true)}
                        className="flex-1 text-sm text-gray-400 hover:text-white transition-colors py-2 px-3 bg-[#1a1d23] rounded-lg"
                      >
                        View Logs
                      </button>
                    </div>
                  )}

                  {!zohoStatus.connected && (
                    <button
                      onClick={handleZohoConnect}
                      className="text-gray-400 hover:text-white text-sm transition-colors text-end mt-4 border-t border-[#22262F] pt-5"
                    >
                      Connect Zoho CRM →
                    </button>
                  )}
                </div>

                {/* Other Integration Cards */}
                {/* Other Integration Cards */}
                {integrations
                  .filter((int) => int.id !== "hubspot" && int.id !== "zoho")
                  .map((integration) => {
                    // Special handling for Salesforce
                    if (integration.id === "salesforce") {
                      return (
                        <div
                          key={integration.id}
                          className="bg-[#0C0E12] border-[#373A41] border rounded-lg p-5 flex flex-col"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                                <img
                                  src="/assets/salesforce.png"
                                  alt="Salesforce logo"
                                  className="w-8 h-8 object-contain"
                                />
                              </div>
                              <div>
                                <h3 className="text-white font-medium">
                                  Salesforce
                                </h3>
                                {salesforceStatus.connected && (
                                  <div className="flex items-center gap-1 mt-1">
                                    <CheckCircle className="w-3 h-3 text-green-500" />
                                    <span className="text-xs text-gray-400">
                                      Connected
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <button
                              onClick={() => handleToggle("Salesforce")}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                salesforceStatus.connected
                                  ? "bg-[#7F56D9]"
                                  : "bg-gray-600"
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  salesforceStatus.connected
                                    ? "translate-x-6"
                                    : "translate-x-1"
                                }`}
                              />
                            </button>
                          </div>

                          <p className="text-gray-400 text-sm flex-grow mb-4">
                            {integration.description}
                          </p>

                          <button
                            onClick={() => {
                              if (salesforceStatus.connected) {
                                // Navigate to detail page or show config modal
                                window.location.href =
                                  "/integrations/salesforce";
                              } else {
                                handleSalesforceConnect();
                              }
                            }}
                            className="text-gray-400 hover:text-white text-sm transition-colors text-end mt-4 border-t border-[#22262F] pt-5"
                          >
                            {salesforceStatus.connected
                              ? "Manage Integration →"
                              : "Connect Salesforce →"}
                          </button>
                        </div>
                      );
                    }

                    // Default card for other integrations
                    return (
                      <div
                        key={integration.id}
                        className="bg-[#0C0E12] border-[#373A41] border rounded-lg p-5 flex flex-col"
                      >
                        {/* existing generic card code */}
                        <div className="flex items-start justify-between mb-10">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                              <img
                                src={getLogoForIntegration(integration.id)}
                                alt={`${integration.name} logo`}
                                className="w-8 h-8 object-contain"
                              />
                            </div>
                            <h3 className="text-white font-medium">
                              {integration.name}
                            </h3>
                          </div>

                          <button
                            onClick={() => handleToggle(integration.name)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              toggleStates[integration.name]
                                ? "bg-[#7F56D9]"
                                : "bg-gray-600"
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                toggleStates[integration.name]
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </button>
                        </div>

                        <p className="text-gray-400 text-sm flex-grow mb-10">
                          {integration.description ||
                            `Connect ${integration.name} to sync your data`}
                        </p>

                        <button className="text-gray-400 hover:text-white text-sm transition-colors text-end mt-10 border-t border-[#22262F] pt-5">
                          View integration →
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Modals */}
            <HubSpotConfigModal
              isOpen={isHubSpotConfigOpen}
              onClose={() => setIsHubSpotConfigOpen(false)}
              integration={hubspotStatus.integration}
              onUpdate={fetchHubSpotStatus}
            />

            <HubSpotSyncLogsModal
              isOpen={isHubSpotLogsOpen}
              onClose={() => setIsHubSpotLogsOpen(false)}
            />

            <ZohoConfigModal
              isOpen={isZohoConfigOpen}
              onClose={() => setIsZohoConfigOpen(false)}
              integration={zohoStatus.integration}
              onUpdate={fetchZohoStatus}
            />

            {isZohoLogsOpen && zohoStatus.integration && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-[#0C0E12] rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
                  <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                    <h3 className="text-white text-lg font-medium">
                      Zoho CRM Sync Logs
                    </h3>
                    <button
                      onClick={() => setIsZohoLogsOpen(false)}
                      className="text-gray-400 hover:text-white"
                    >
                      <XCircle className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="overflow-y-auto max-h-[calc(80vh-80px)]">
                    <ZohoSyncLogs
                      companyId={zohoStatus.integration.companyId}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "billing":
        return (
          <div className="space-y-6">
            {/* Current Plan */}
            <div className="bg-[#0C0E12] border border-[#373A41] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-6 h-6 text-[#7F56D9]" />
                <h3 className="text-white text-lg font-medium">Current Plan</h3>
              </div>

              <div className="flex items-center justify-between mb-6">
                <div>
                  <h4 className="text-white text-2xl font-semibold mb-1">
                    {billingInfo.plan} Plan
                  </h4>
                  <p className="text-gray-400">
                    ${billingInfo.amount}/
                    {billingInfo.billingCycle === "monthly" ? "month" : "year"}
                  </p>
                </div>
                <button className="px-6 py-2 bg-[#7F56D9] hover:bg-[#6B46C1] text-white rounded-lg transition-colors">
                  Upgrade Plan
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t border-gray-700">
                <div>
                  <div className="text-sm text-gray-400 mb-1">
                    Next billing date
                  </div>
                  <div className="text-white flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(billingInfo.nextBillingDate).toLocaleDateString(
                      "en-US",
                      {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      },
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">
                    Payment method
                  </div>
                  <div className="text-white flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    •••• {billingInfo.paymentMethod.last4}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-[#0C0E12] border border-[#373A41] rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-6 h-6 text-[#7F56D9]" />
                  <h3 className="text-white text-lg font-medium">
                    Payment Method
                  </h3>
                </div>
                <button className="text-[#7F56D9] hover:text-[#6B46C1] text-sm transition-colors">
                  Update
                </button>
              </div>

              <div className="flex items-center gap-4 p-4 bg-[#1a1d23] rounded-lg">
                <div className="w-12 h-8 bg-white rounded flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-gray-800" />
                </div>
                <div className="flex-1">
                  <div className="text-white font-medium">
                    •••• •••• •••• {billingInfo.paymentMethod.last4}
                  </div>
                  <div className="text-sm text-gray-400">
                    Expires {billingInfo.paymentMethod.expiry}
                  </div>
                </div>
              </div>
            </div>

            {/* Billing History */}
            <div className="bg-[#0C0E12] border border-[#373A41] rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-[#7F56D9]" />
                  <h3 className="text-white text-lg font-medium">
                    Billing History
                  </h3>
                </div>
              </div>

              <div className="space-y-3">
                {[
                  {
                    date: "Nov 15, 2024",
                    amount: 99,
                    status: "Paid",
                    invoice: "INV-2024-11",
                  },
                  {
                    date: "Oct 15, 2024",
                    amount: 99,
                    status: "Paid",
                    invoice: "INV-2024-10",
                  },
                  {
                    date: "Sep 15, 2024",
                    amount: 99,
                    status: "Paid",
                    invoice: "INV-2024-09",
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-[#1a1d23] rounded-lg hover:bg-[#22262F] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-green-900/20 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <div className="text-white font-medium">
                          ${item.amount.toFixed(2)}
                        </div>
                        <div className="text-sm text-gray-400">{item.date}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-green-900/20 text-green-400 rounded-full text-xs font-medium">
                        {item.status}
                      </span>
                      <button className="text-gray-400 hover:text-white transition-colors">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <h1 className="text-2xl font-semibold mb-4 sm:mb-0">Settings</h1>
        </div>

        <div className="mb-6 overflow-x-auto">
          <nav className="flex space-x-6 sm:space-x-8 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-1 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "text-[#CECFD2] bg-[#22262F] px-3 py-3 rounded"
                    : "text-gray-400 hover:text-gray-300 px-3 py-3"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {renderTabContent()}
      </div>
    </div>
  );
};

export default SettingsPage;
